// Was beim Klick auf „Speichern" in der Kontaktakte tatsächlich zum Server geht.
//
// Bis 08/2026 ging der GESAMTE geladene Kontakt zurück — auch jedes Feld, das
// niemand angefasst hatte, mit dem Stand vom Ladezeitpunkt. Hatten zwei Kollegen
// dieselbe Akte offen, setzte der zweite Speichervorgang die Änderung des ersten
// kommentarlos auf dessen alten Wert zurück: Es entstand kein zusammengeführter
// Stand, sondern nur „A" oder „B". Dafür brauchte es nicht einmal Gleichzeitigkeit
// — eine morgens geöffnete und abends gespeicherte Akte warf alles dazwischen weg.
//
// Deshalb geht jetzt nur noch mit, was sich seit dem Laden wirklich geändert hat.
// Das Backend schreibt über `repo.preload` genau die mitgeschickten Spalten
// (contact.service.ts); was nicht im Body steht, bleibt unberührt. Zwei Anwender,
// die verschiedene Felder pflegen, überschreiben sich damit nicht mehr.
//
// GRENZE: Verglichen wird je Feld. Listen — Kommunikation, Adressen, Bank­ver­
// bindungen, Verfügungen — gehen weiterhin als GANZES mit, sobald eine einzige
// Zeile darin abweicht; innerhalb einer Liste gilt also weiter „wer zuletzt
// speichert, gewinnt". Das Backend nimmt diese Listen nur vollständig entgegen.

import { formToApi } from "../../utils/contactDetailMapper";
import { formatIbanForDisplay, normalizeBic } from "../forms/validation/iban";
import { geschlechtFuerSpeichern } from "../../utils/anredeGeschlecht";
import type { ContactDetailsFormValues } from "../../types/Contact";
import {
  zuwendungenAusFeld,
  zuwendungenFuerApi,
} from "../../data/zuwendungenForm";

/** Rohbau eines Kontakt-Bodys, wie ihn PUT /contact/:id entgegennimmt. */
export type SpeicherPayload = Record<string, unknown>;

/**
 * Die Maske pflegt Straße/PLZ/Ort und IBAN/BIC als flache Felder, gespeichert
 * werden sie als Zeile in `addresses` bzw. `bankAccounts`. Der Patch geht in die
 * als Standard markierte Zeile, sonst in die erste — und legt eine an, wenn es
 * noch keine gibt und wirklich etwas eingetragen wurde.
 */
const mergeIntoDefault = <T extends { isDefault?: boolean }>(
  list: T[],
  patch: Partial<T>,
): T[] => {
  const hasContent = Object.values(patch).some(
    (v) => v !== undefined && v !== null && v !== "",
  );
  if (list.length === 0) {
    return hasContent ? [{ ...(patch as object), isDefault: true } as T] : list;
  }
  const found = list.findIndex((r) => r.isDefault);
  const idx = found !== -1 ? found : 0;
  const next = [...list];
  next[idx] = { ...next[idx], ...patch, isDefault: true } as T;
  return next;
};

/**
 * Formularstand → vollständiger Kontakt-Body. Wird ZWEIMAL aufgerufen: einmal
 * für den aktuellen Stand, einmal für den Stand beim Laden. Nur weil beide
 * Seiten durch dieselbe Funktion laufen, ist der Vergleich darunter belastbar —
 * er sieht sonst Unterschiede, die nur aus der Umformung stammen.
 */
/**
 * Löst eine Anrede-Katalog-ID in ihr Label auf („2" → „Herr"). Wird von außen
 * gereicht, weil nur der Hook den Katalog kennt.
 */
export type AnredeLabel = (anredeId: string | null | undefined) => string | undefined;

/**
 * Vorbelegter Kontoinhaber für einen Formularstand (Name in Kontoschreibweise,
 * ohne Rechtsform-Kürzel) — von außen gereicht, damit die Regel an einer
 * Stelle bleibt (utils/kontoinhaber.ts).
 */
export type KontoinhaberFuer = (cd: ContactDetailsFormValues) => string;

const gefuellt = (v: unknown) => String(v ?? "").trim() !== "";

export function speicherPayload(
  cd: ContactDetailsFormValues,
  anredeLabel?: AnredeLabel,
  kontoinhaberFuer?: KontoinhaberFuer,
): SpeicherPayload {
  const updatedAddresses = mergeIntoDefault(cd.addresses ?? [], {
    street: cd.strasse,
    postalCode: cd.plz,
    city: cd.ort,
    ortsteil: cd.ortsteil,
    // Land: nur mitschreiben, wenn es in der Maske steht — sonst würde eine
    // Adresse ohne Landangabe beim Speichern auf leer gesetzt.
    ...(cd.land ? { country: cd.land } : {}),
    type: cd.defaultAddress?.type || "private",
  } as any);

  const updatedBankAccounts = mergeIntoDefault(cd.bankAccounts ?? [], {
    // Einheitliche Form über alle Eingangstüren: Die IBAN wird gruppiert
    // abgelegt (so liegt sie seit jeher in der Datenbank), der BIC ohne
    // Leerzeichen und in Großbuchstaben. Ohne das vergleicht die
    // Dubletten-Erkennung „DE42 3905…" und „DE423905…" als zwei Konten.
    iban: cd.iban ? formatIbanForDisplay(String(cd.iban)) : cd.iban,
    bic: cd.bic ? normalizeBic(String(cd.bic)) : cd.bic,
    bankName: cd.kreditinstitut,
    bankDescription: cd.defaultBankAccount?.bankDescription,
    accountHolder: cd.accountHolder,
    directDebit: cd.directDebit,
  } as any);
  // Kontoinhaber vorbelegen (02.10.2026): Trägt die Standard-Bankverbindung
  // Angaben, aber keinen Inhaber, wird der Name des Kontakts geschrieben — das
  // Feld zeigt ihn ohnehin an. Bewusst NACH mergeIntoDefault: der Name allein
  // legt keine Bankverbindung an. Beide Stände des Diffs laufen hier durch;
  // geschrieben wird er also, sobald die Bankverbindung ohnehin mitgeht.
  const vorgabe = kontoinhaberFuer?.(cd) ?? "";
  if (vorgabe) {
    for (let i = 0; i < updatedBankAccounts.length; i++) {
      const zeile = updatedBankAccounts[i] as Record<string, unknown>;
      if (
        zeile.isDefault &&
        !gefuellt(zeile.accountHolder) &&
        [zeile.iban, zeile.bic, zeile.bankName].some(gefuellt)
      ) {
        updatedBankAccounts[i] = { ...zeile, accountHolder: vorgabe } as any;
      }
    }
  }

  const api = formToApi(cd);

  // Das Geschlecht wird seit 08/2026 nicht mehr eigens erfasst, sondern folgt
  // der Anrede. Die Ableitung sitzt hier und nicht in der Maske, damit sie auch
  // dann greift, wenn die Anrede über einen anderen Weg gesetzt wurde — und
  // damit der Feld-Diff sie als das sieht, was sie ist: eine Folge der
  // geänderten Anrede, die mitgeschickt werden muss.
  if (anredeLabel) {
    api.gender = geschlechtFuerSpeichern(anredeLabel(cd.anrede), cd.geschlecht);
  }

  return {
    ...api,
    addresses: updatedAddresses,
    bankAccounts: updatedBankAccounts,
    // Die Zuwendungen liegen in der Maske als FORMULAR-Einträge (String-`id`
    // fürs Rendern, Server-Id in `dbId`). Der Server kennt nur die API-Form —
    // ohne diese Umwandlung käme eine String-Id an, der Abgleich hielte JEDE
    // Zeile für neu und legte sie bei jedem Speichern erneut an.
    ...(cd.zuwendungen !== undefined
      ? { zuwendungen: zuwendungenFuerApi(zuwendungenAusFeld(cd.zuwendungen)) }
      : {}),
  };
}

/**
 * Schlüssel rekursiv sortieren. Ohne diesen Schritt gälte eine Liste schon dann
 * als geändert, wenn eine Zeile darin durch die Antwort eines anderen Endpoints
 * ersetzt wurde und ihre Felder nur in anderer Reihenfolge trägt — der Payload
 * wüchse ohne Anlass wieder auf die alte Größe.
 */
const sortiert = (wert: unknown): unknown => {
  if (Array.isArray(wert)) return wert.map(sortiert);
  if (wert && typeof wert === "object") {
    const o = wert as Record<string, unknown>;
    return Object.keys(o)
      .sort()
      .reduce<Record<string, unknown>>((acc, k) => {
        acc[k] = sortiert(o[k]);
        return acc;
      }, {});
  }
  return wert;
};

const vergleichswert = (wert: unknown): string | undefined => JSON.stringify(sortiert(wert));

/**
 * Die eigentliche Zusammenführung: nur die Felder, die sich gegenüber dem
 * geladenen Stand unterscheiden.
 *
 * Verglichen werden die Schlüssel des NEUEN Standes — beide Seiten entstehen aus
 * `speicherPayload`, tragen also dieselben Schlüssel. Ein Feld, das nur der alte
 * Stand hätte, ließe sich ohnehin nicht übertragen: `undefined` fällt beim
 * Serialisieren aus dem Body und würde die Spalte nicht leeren, sondern nur
 * unberührt lassen.
 *
 * Ein geleertes Feld kommt dagegen als "" oder `null` an und geht mit — Leeren
 * ist eine Änderung wie jede andere.
 */
export function nurGeaenderteFelder(
  neu: SpeicherPayload,
  alt: SpeicherPayload,
): SpeicherPayload {
  const geaendert: SpeicherPayload = {};
  for (const feld of Object.keys(neu)) {
    if (vergleichswert(neu[feld]) !== vergleichswert(alt[feld])) geaendert[feld] = neu[feld];
  }
  return geaendert;
}
